import type { ReactNode } from 'react'

import { faGripLines } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/utils'
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  useDndContext,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core'

import { reorderIds } from './sub-timers'
import type { DropSide } from './sub-timers'

import type { DragEndEvent } from '@dnd-kit/core'

// Reordering a cue's sub timers, styled after cue reordering: a grip handle
// starts the drag, an overlay follows the pointer while the original is hidden,
// and a dashed blue line marks where the timer will land.
//
// Its own DndContext, nested inside the cue list's: drags stay within one cue.

const dropId = (timerId: string, side: DropSide) => `${timerId}|${side}`

function parseDropId(id: string): { timerId: string; side: DropSide } {
  const sep = id.lastIndexOf('|')
  return { timerId: id.slice(0, sep), side: id.slice(sep + 1) as DropSide }
}

type SubTimerSortableProps = {
  ids: readonly string[]
  onReorder: (ids: string[]) => void
  /** Renders one timer; `overlay` is true for the copy following the pointer. */
  renderItem: (id: string, overlay: boolean) => ReactNode
}

export function SubTimerSortable({
  ids,
  onReorder,
  renderItem,
}: SubTimerSortableProps) {
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return
    const next = reorderIds(
      ids,
      String(active.id),
      parseDropId(String(over.id)),
    )
    if (next) onReorder(next)
  }

  return (
    <DndContext
      collisionDetection={pointerWithin}
      onDragEnd={onDragEnd}
    >
      {ids.map((id) => (
        <SubTimerDraggable
          key={id}
          timerId={id}
        >
          {renderItem(id, false)}
        </SubTimerDraggable>
      ))}
      <SubTimerOverlay renderItem={renderItem} />
    </DndContext>
  )
}

function SubTimerOverlay({
  renderItem,
}: Pick<SubTimerSortableProps, 'renderItem'>) {
  const { active } = useDndContext()
  return (
    <DragOverlay>
      {active && (
        <div className="shadow-lg">{renderItem(String(active.id), true)}</div>
      )}
    </DragOverlay>
  )
}

/**
 * A timer, made draggable, with a drop zone over each half.
 *
 * The wrapper is the draggable node while the grip lives in the timer's bar
 * (see {@link SubTimerDragHandle}), so the whole card moves but only the grip
 * starts a drag.
 */
function SubTimerDraggable({
  timerId,
  children,
}: {
  timerId: string
  children: ReactNode
}) {
  const { setNodeRef, isDragging } = useDraggable({ id: timerId })

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'relative',
        // The overlay stands in while dragging, so hide the original
        isDragging && 'opacity-0',
      )}
    >
      {children}
      <DropZone
        timerId={timerId}
        side="before"
        disabled={isDragging}
      />
      <DropZone
        timerId={timerId}
        side="after"
        disabled={isDragging}
      />
    </div>
  )
}

function DropZone({
  timerId,
  side,
  disabled,
}: {
  timerId: string
  side: DropSide
  disabled: boolean
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: dropId(timerId, side),
    disabled,
  })
  const before = side === 'before'

  return (
    <div
      ref={setNodeRef}
      role="none"
      // Hit-testing uses the rect, so the zone never has to take the pointer
      className={cn(
        'pointer-events-none absolute inset-x-0 h-1/2',
        before ? 'top-0' : 'bottom-0',
      )}
    >
      {isOver && (
        // Sits in the gap between timers
        <div
          className={cn(
            'absolute inset-x-0 border-dashed border-blue-500',
            before ? '-top-[3px] border-t-2' : '-bottom-[3px] border-b-2',
          )}
        />
      )}
    </div>
  )
}

const handleClass =
  'flex w-1/4 flex-none items-center justify-center rounded-sm text-[10px] text-white/60 opacity-0 group-hover/timer:opacity-100 focus-visible:opacity-100 outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-white/60'

/** The grip that starts a drag. Joined to the card by id. */
export function SubTimerDragHandle({ timerId }: { timerId: string }) {
  const { listeners, setActivatorNodeRef } = useDraggable({ id: timerId })

  return (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...listeners}
      aria-label="Reorder"
      title="Drag to reorder"
      className={cn(handleClass, 'cursor-grab enabled:hover:bg-white/10')}
    >
      <FontAwesomeIcon icon={faGripLines} />
    </button>
  )
}

/** The grip for a lone timer: in place, but there is nothing to reorder. */
export function SubTimerDragHandleDisabled() {
  return (
    <button
      type="button"
      disabled
      aria-label="Reorder"
      title="Add another sub-timer to reorder"
      className={cn(
        handleClass,
        'cursor-not-allowed group-hover/timer:opacity-40',
      )}
    >
      <FontAwesomeIcon icon={faGripLines} />
    </button>
  )
}

/** The grip as drawn in the overlay: same look, no drag wiring. */
export function SubTimerDragHandleStatic() {
  return (
    <span className={cn(handleClass, 'cursor-grabbing opacity-100')}>
      <FontAwesomeIcon icon={faGripLines} />
    </span>
  )
}
